import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useOrder } from '@app/api/cards/useOrder';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Note from '@shared/ui/messages/Note';
import { ThemeButtonIcon } from '@shared/ui/buttons';
import { copyToClipboard, formatWalletAddress } from '@utils/helpers';
import { formatTimeRemaining, formatDateTime } from '@utils/date-helpers';
import { useCountdownTimer } from '@utils/hooks/useCountdownTimer';
import { getCryptoName } from '@utils/balance-helpers';
import { currencySymbols } from '@utils/static';
import { icons } from '@shared/static/icons';
import QRCode from 'react-qr-code';
import { CopyToClipWhisper } from '@shared/ui/messages/CopyToClipWhisper';
import { ListData, Status } from '@shared/ui/parts';
import AuthLayout from '@shared/ui/auth/AuthLayout';
import { useTranslation } from 'react-i18next';

export function OrderIdPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { fetchOrderByIdPublic } = useOrder();
  const [order, setOrder] = useState(null);
  const timeRemaining = useCountdownTimer(order?.expired_at);

  useEffect(() => {
    if (id) {
      setOrder(null);
      fetchOrderByIdPublic(id, setOrder);
    }
  }, [id]);

  const listData = order
    ? [
        {
          label: t('top_up_fee'),
          value: `${order.bank_fee || order.bank_to?.top_up_fee || ''} %`,
        },
        {
          label: t('deposit_amount'),
          value: `${order.amount_to || ''} ${currencySymbols[order.currency_to] || ''}`,
        },
        {
          label: t('date_time'),
          value: formatDateTime(order.created_at),
        },
        {
          label: t('order_id'),
          value: order.order_id || '',
          isCopy: true,
        },
      ]
    : [];

  return (
    <AuthLayout>
      <section className="auth customDialog">
        <div className="topUpOrder__share">
          <DialogTitle id="scroll-dialog-title">
            <div className="customDialog__heading">
              <div>
                <div className={`has-subtitle`}>
                  <p className={`customDialog__heading-title title-h3m`}>
                    {t('top_up_order')}
                  </p>
                </div>
                <div className="d-flex body-14r secondary-text">
                  {/* <Status status={order?.status} /> */}
                </div>
              </div>
              <div className="customDialog__heading-actions"></div>
            </div>
          </DialogTitle>
          <DialogContent>
            <Note
              marginBottom
              type="warning"
              title={t('important_choose_correct_network')}
              content={t('selecting_wrong_network_note')}
            />
            <div className="topUpOrder">
              <div>
                <p className="body-18m">{t('top_up_amount')}</p>
                <div className="topUpOrder__balance">
                  <p>
                    {order?.amount_from} {getCryptoName(order?.currency_from)}
                  </p>
                  <ThemeButtonIcon
                    onClick={() => copyToClipboard(order?.amount_from)}
                    icon="copy"
                  />
                </div>
                <ListData listData={listData} />
              </div>
              <div className="topUpOrder__info">
                <div className="topUpOrder__network body-18m">
                  {icons[getCryptoName(order?.currency_from)]}
                  <p>{order?.currency_from}</p>
                </div>
                <div className="topUpOrder__qr">
                  {order?.address && <QRCode value={order?.address} />}
                </div>
                <div className="topUpOrder__links no-grid">
                  <div className="topUpOrder__links-item">
                    <p className="secondary-text">{t('wallet_address')}</p>
                    <CopyToClipWhisper
                      value={order?.address}
                      display={formatWalletAddress(order?.address)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <div className="topUpOrder__valid">
              <p className="secondary-text">{t('valid_for')}:</p>
              <p className="title-h3m">{formatTimeRemaining(timeRemaining)}</p>
            </div>
          </DialogActions>
        </div>
      </section>
    </AuthLayout>
  );
}
